import { FormControl, FormGroup } from '@angular/forms'

import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { of } from 'rxjs/internal/observable/of'

import {
  ApiError,
  ApiResponse,
  HttpStatusCodesEnum,
  PaymentStatusesEnum,
  PaymentsService,
  PaymentStatusTriggerModel,
} from '@spa/api'

import { BaseControl, Helper, SelectControl } from '../../../shared/classes'
import { SelectOptionInterface } from '../../../shared/components'

import { TransactionStatusesFormErrorStatesEnum } from '../enums'

import {
  BulkPaymentStatusesControls,
  TransactionStatusSelector,
  TransactionNoteSelector,
} from '../../../../assets/descriptors'
import { ServerResultInterface } from '@spa/api/lib/common'
import { BulkPaymentModalDataInterface } from '../interfaces'

export class BulkPaymentStatusesForm {
  private paymentsService: PaymentsService

  public formGroup: FormGroup
  public controls: BaseControl[]

  constructor(transactionsService: PaymentsService) {
    this.paymentsService = transactionsService
  }

  initForm(payments: BulkPaymentModalDataInterface[]): void {
    this.formGroup = new FormGroup({})

    this.controls = BulkPaymentStatusesControls().map((control: BaseControl) => {
      switch (control.name) {
        case TransactionNoteSelector:
          this.formGroup.addControl(control.name, new FormControl(control.value, control.validators))
          break
        case TransactionStatusSelector:
          const ctrl = control as SelectControl
          const options = payments.reduce((options: SelectOptionInterface[], item: BulkPaymentModalDataInterface) => {
            const currentTrigger = Helper.enumsHelper().getPaymentStatusTriggerByStatus(item.payment.paymentStatus)
            const availableTriggers = item.triggers.filter((t) => currentTrigger !== t)
            availableTriggers.forEach((trigger) => {
              const availableTriggerItem = Helper.enumsHelper().getPaymentStatusByTrigger(trigger)

              const notExists = options.findIndex((o) => o.value == availableTriggerItem) < 0
              if (notExists) {
                options.push({
                  label: Helper.textsHelper().getPaymentStatusName(availableTriggerItem),
                  value: availableTriggerItem,
                })
              }
            })

            return options
          }, [])
          ctrl.options = options
          this.formGroup.addControl(control.name, new FormControl(control.value, control.validators))
          break
      }
      return control
    })
  }

  validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string }): boolean {
    if (this.formGroup.invalid) {
      Object.keys(this.controls).forEach((key: string) => {
        const control = this.controls[key]
        const formControl = this.formGroup.get(control.name)

        if (formControl && formControl.invalid) {
          const errorKeys = Object.keys(formControl.errors)

          const errors = errorKeys.map((errorKey: string) => {
            return messageFunction(control.placeholder)[errorKey]
          })

          control.setErrors(errors)
        }
      })

      return false
    }

    return true
  }

  createRequest(
    payments: BulkPaymentModalDataInterface[],
    updateStatusToCancelledPermissions: string[],
    authUserPermissions: string[],
  ): Observable<ApiResponse<ApiError[] | boolean>> {
    const status = this.formGroup.value[TransactionStatusSelector]
    const trigger = Helper.enumsHelper().getPaymentStatusTriggerByStatus(status)
    const note = this.formGroup.value[TransactionNoteSelector]
    if (
      status === PaymentStatusesEnum.Canceled &&
      !Helper.getUnionStrategyPermissionsCheck(updateStatusToCancelledPermissions, authUserPermissions)
    ) {
      const error = new ApiError(TransactionStatusesFormErrorStatesEnum.UpdateStatusToCancelledRejected)
      const errorResponse = new ApiResponse(TransactionStatusesFormErrorStatesEnum.UpdateStatusToCancelledRejected, [
        error,
      ])

      return of(errorResponse)
    }
    const errors = []
    payments
      .filter((s) => !s.triggers.includes(trigger))
      .forEach((p) => {
        const error = new ApiError(TransactionStatusesFormErrorStatesEnum.UpdateStatusUnavailableTrigger, p.referenceId)
        errors.push(error)
      })
    const models = payments
      .filter((s) => s.triggers.includes(trigger))
      .map((p) => {
        return {
          referenceId: p.referenceId,
          model: new PaymentStatusTriggerModel(
            trigger,
            p.payment.paymentProcess,
            p.payment.paymentMethod,
            p.payment.paymentProvider,
            p.payment.paymentReference,
            note,
          ),
        }
      })
    const requestModels = models.map((i) => i.model)
    return this.paymentsService.changePaymentStatuses(requestModels).pipe(
      map((response: ApiResponse<ApiError[] | ServerResultInterface<boolean>[]>) => {
        if (response.status !== HttpStatusCodesEnum.Ok) {
          const result = (response.data as ApiError[]) ?? []
          result.forEach((r: ApiError, index: number) => {
            const data = r.data as ServerResultInterface<boolean>
            const error = new ApiError(
              data.result_code === HttpStatusCodesEnum.NoContent
                ? HttpStatusCodesEnum.NoContent
                : TransactionStatusesFormErrorStatesEnum.UpdateStatusFailed,
              models[index].referenceId,
            )
            errors.push(error)
          })
        }
        if (errors.length > 0) {
          if (response.status == HttpStatusCodesEnum.Ok) {
            models.forEach((m) => {
              const error = new ApiError(HttpStatusCodesEnum.NoContent, m.referenceId)
              errors.push(error)
            })
          }
          const errorResponse = new ApiResponse(HttpStatusCodesEnum.BadRequest, errors)
          return errorResponse
        }

        return Helper.createSuccessResponse<boolean>(true)
      }),
    )
  }
}

